// src/Dashboard.js
import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';

function Dashboard() {
  const [userData, setUserData] = useState(null);
  const [dailyChallenges, setDailyChallenges] = useState([]); // Define challenges state here
  const [weeklyChallenges, setWeeklyChallenges] = useState([]);
  const [newChallenges, setNewChallenges] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [timedChallenges, setTimedChallenges] = useState([]);

  // Fetch authenticated user info from the API endpoint
  useEffect(() => {
    fetch("https://auth.fnrewards.com/api/user", {
      method: "GET",
      credentials: "include"
    })
      .then(res => {    
        if (!res.ok) {
          // If not authorized, redirect to login/homepage
          if (res.status === 401) {
            navigate("/"); // or navigate("/login") if you have a dedicated login page
          }
          throw new Error("Failed to fetch user data");
        }
        return res.json();
      })
      .then(data => {
        setUserData(data);
        setLoading(false);
      })  
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, [navigate]);

  // Fetch new challenges
  useEffect(() => {
    fetch("https://auth.fnrewards.com/api/daily-challenges", {
      method: "GET",
      credentials: "include"
    })
      .then(res => res.json())
      .then(data => {
        // Check the response structure; adjust key if needed.
        if (data.dailyChallenges) {
          setDailyChallenges(data.dailyChallenges);
        } else {
          console.error("Response structure unexpected:", data);
        }
      })
      .catch(err => console.error("Error fetching challenges:", err));
  }, []);

  useEffect(() => {
    fetch("https://auth.fnrewards.com/api/weekly-challenges", {
      method: "GET",
      credentials: "include"
    })
      .then(res => res.json())
      .then(data => {
        // Check the response structure; adjust key if needed.
        if (data.weeklyChallenges) {
          setWeeklyChallenges(data.weeklyChallenges);
        } else {
          console.error("Response structure unexpected:", data);
        }
      })
      .catch(err => console.error("Error fetching challenges:", err));
  }, []);

  useEffect(() => {
    fetch("https://auth.fnrewards.com/api/new-challenges", {
      method: "GET",
      credentials: "include"
    })
      .then(res => res.json())
      .then(data => {
        // Check the response structure; adjust key if needed.
        if (data.newChallenges) {
          setNewChallenges(data.newChallenges);
        } else {
          console.error("Response structure unexpected:", data);
        }
      })
      .catch(err => console.error("Error fetching challenges:", err));
  }, []);

  // Fetch timed challenges (you need an endpoint for that, e.g., /api/timed-challenges)
  useEffect(() => {
    fetch("https://auth.fnrewards.com/api/timed-challenges", {
      method: "GET",
      credentials: "include"
    })
      .then(res => res.json())
      .then(data => setTimedChallenges(data.timedChallenges))
      .catch(err => console.error("Error fetching timed challenges:", err));
  }, []);

  const handleStartChallenge = async (challengeId) => {
    try {
      const res = await fetch("https://auth.fnrewards.com/api/challenge/start", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ challengeId })
      });
      const data = await res.json();
      console.log(data.message);
      // Optionally update UI or start tracking progress
    } catch (err) {
      console.error("Error starting challenge:", err);
    }
  };

  const handleCheckChallenge = async (challengeId) => {
    try {
      const res = await fetch("https://auth.fnrewards.com/api/challenge/check", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ challengeId })
      });
      const data = await res.json();
      console.log(data.message, "Progress:", data.newProgress);
    } catch (err) {
      console.error("Error checking challenge progress:", err);
    }
  };

  const handleLogout = async () => {
    try {
      const res = await fetch("https://auth.fnrewards.com/api/logout", {
        method: "POST",
        credentials: "include"
      });
      if (res.ok) {
        navigate("/");
      } else {
        console.error("Logout failed");
      }
    } catch (err) {
      console.error("Error logging out:", err);
    }
  };

  if (loading) return <div>Loading dashboard...</div>;
  if (error) return <div>Error: {error}</div>;

  return (
    <div style={{ padding: "20px" }}>
      <h1>Dashboard</h1>
      {userData && (
        <div>
          <p>Welcome, {userData.first_name} {userData.last_name}!</p>
          <p>Email: {userData.email}</p>
          <p>Credits: {userData.credits}</p>
        </div>
      )}
      
      <h2>Today's Daily Challenges</h2>
      {dailyChallenges.length > 0 ? (
        <ul>
          {dailyChallenges.map(challenge => (
            <li key={challenge.id}>
              <strong>{challenge.title}</strong>: {challenge.description} <br />
              Requirement: {challenge.requirement} (stat: {challenge.stat_key}), Reward: {challenge.reward} credits <br />
              <button onClick={() => handleStartChallenge(challenge.id)}>Start Challenge</button>
              &nbsp;
              <button onClick={() => handleCheckChallenge(challenge.id)}>Check Progress</button>
            </li>
          ))}
        </ul>
      ) : (
        <p>No daily challenges available.</p>
      )}
      
      <h2>This Week's Challenges</h2>
      {weeklyChallenges.length > 0 ? (
        <ul>
          {weeklyChallenges.map(challenge => (
            <li key={challenge.id}>
              <strong>{challenge.title}</strong>: {challenge.description} <br />
              Requirement: {challenge.requirement} (stat: {challenge.stat_key}), Reward: {challenge.reward} credits
              <br />
              <button onClick={() => handleStartChallenge(challenge.id)}>Start Challenge</button>
              &nbsp;
              <button onClick={() => handleCheckChallenge(challenge.id)}>Check Progress</button>
            </li>
          ))}
        </ul>
      ) : (
        <p>No weekly challenges available.</p>
      )}

      <h2>New-User Challenges</h2>
      {newChallenges.length > 0 ? (
        <ul>
          {newChallenges.map(challenge => (
            <li key={challenge.id}>
              <strong>{challenge.title}</strong>: {challenge.description} <br />
              Requirement: {challenge.requirement} (stat: {challenge.stat_key}), Reward: {challenge.reward} credits
              <br />
              <button onClick={() => handleStartChallenge(challenge.id)}>Start Challenge</button>
              &nbsp;
              <button onClick={() => handleCheckChallenge(challenge.id)}>Check Progress</button>
            </li>
          ))}
        </ul>
      ) : (
        <p>No new-user challenges available.</p>
      )}

      <h2>Timed Monday Challenges</h2>
      {timedChallenges.length > 0 ? (
        <ul>
          {timedChallenges.map(challenge => (
            <li key={challenge.id}>
              <strong>{challenge.title}</strong>: {challenge.description} <br />
              Requirement: {challenge.requirement} (stat: {challenge.stat_key}), Reward: {challenge.reward} credits
              <br />
              <button onClick={() => handleStartChallenge(challenge.id)}>Start Challenge</button>
              &nbsp;
              <button onClick={() => handleCheckChallenge(challenge.id)}>Check Progress</button>
            </li>
          ))}
        </ul>
      ) : (
        <p>No weekly challenges available.</p>
      )}
      <button onClick={handleLogout}>Logout</button>
      <button ><Link to="/marketplace">Marketplace</Link></button>
    </div>
  );
}

export default Dashboard;